import { useMemo } from "react";
import { type FragmentType, useFragment } from "@repo/graphql-types";
import { CpsAlert } from "corpus";
import { Link, useNavigate, useRouteContext } from "@tanstack/react-router";
import { Bell, Right } from "@repo/icons";
import { PatientDetailLastAppointmentFragment } from "@/components/patient-detail/patient-detail-buttons";
import {
  EmptyStateV2Content,
  EmptyStateV2Description,
  EmptyStateV2Icon,
  EmptyStateV2Root,
} from "@/components/empty-state-v2";
import { Button } from "@/components/button";

interface PatientDetailReminderAlertProps {
  data: FragmentType<typeof PatientDetailLastAppointmentFragment>;
}

export const PatientDetailReminderAlert = ({
  data,
}: PatientDetailReminderAlertProps): JSX.Element | null => {
  const navigate = useNavigate();

  const { flags } = useRouteContext({ strict: false });
  const showNewPatientLayout = useMemo(
    () => flags["exibe-nova-tela-de-paciente"],
    [flags],
  );

  const { lastAppointment } = useFragment(PatientDetailLastAppointmentFragment, data);

  const appointmentId = lastAppointment[0]?.codAgendamento;

  const showReminderButton = lastAppointment[0]?.reminders.some(
    (reminder) => reminder.codAgendamento === appointmentId,
  );

  if (!showReminderButton) {
    return null;
  }

  if (showNewPatientLayout) {
    return (
      <CpsAlert
        type="info"
        title="Esse paciente possui um lembrete programado"
        linkTitle="Ver lembrete"
        linkPosition="bottom"
        linkProps={{
          onClick: () =>
            void navigate({
              to: "/settings/assist/follow-up-appointment-reminder/",
              search: {
                navigateBack: true,
              },
            }),
        }}
      />
    );
  }

  return (
    <EmptyStateV2Root className="items-start bg-secondary-50 p-3 rounded-40 gap-3">
      <EmptyStateV2Content>
        <EmptyStateV2Icon
          className="bg-white p-2 stroke-0 fill-secondary-400"
          Icon={Bell}
        />
        <EmptyStateV2Description className="text-secondary-500 font-medium text-sm">
          Este paciente já possui um ou mais lembretes programados.
        </EmptyStateV2Description>
      </EmptyStateV2Content>

      <Link
        to="/settings/assist/follow-up-appointment-reminder/"
        search={{
          navigateBack: true,
        }}
      >
        <Button className="px-0 min-h-0 w-fit text-xs" type="button" rounded size="small">
          <div className="flex flex-row gap-1 pl-3 pr-1">
            Ver lembretes <Right className="pr-0" size={16} />
          </div>
        </Button>
      </Link>
    </EmptyStateV2Root>
  );
};
